import { useState } from "react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import {
  useTicketParticipants,
  useEligibleParticipants,
  useAddParticipant,
  useRemoveParticipant,
} from "../../hooks/useTicketParticipants";
import { showToast } from "../../lib/toast";
import { getErrorMessage } from "../../lib/apiErrors";
import {
  Users,
  UserPlus,
  User,
  X,
  Loader2,
  Search,
  Mail,
  Check,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import type { ParticipantResponse, EligibleParticipantResponse } from "../../types/tickets";

interface TicketParticipantsProps {
  ticketId: string;
  organizationId?: string;
  embedded?: boolean; // If true, renders without Card wrapper (for embedding in another section)
  readOnly?: boolean; // Hides add/remove controls - merged tickets are read-only
}

export function TicketParticipants({ ticketId, organizationId, embedded = false, readOnly = false }: TicketParticipantsProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState<EligibleParticipantResponse[]>([]);
  const [isAdding, setIsAdding] = useState(false);

  const { data: participants = [], isLoading } = useTicketParticipants(ticketId, organizationId);
  const { data: eligibleParticipants = [], isLoading: isSearching } = useEligibleParticipants(
    ticketId,
    organizationId,
    searchQuery
  );
  const addParticipantMutation = useAddParticipant();
  const removeParticipantMutation = useRemoveParticipant();

  const handleToggleSelection = (customer: EligibleParticipantResponse) => {
    setSelectedCustomers(prev => {
      const isSelected = prev.some(c => c.id === customer.id);
      if (isSelected) {
        return prev.filter(c => c.id !== customer.id);
      } else {
        return [...prev, customer];
      }
    });
  };

  const handleAddSelectedParticipants = async () => {
    if (selectedCustomers.length === 0) return;

    setIsAdding(true);
    // Participants are added one request at a time, so a failure can land partway through
    let addedCount = 0;
    try {
      // Add all selected participants
      for (const customer of selectedCustomers) {
        await addParticipantMutation.mutateAsync({
          ticketId,
          customerId: customer.id,
          organizationId,
        });
        addedCount++;
      }

      const count = selectedCustomers.length;
      showToast.success(
        "Participants Added",
        count === 1
          ? `${selectedCustomers[0].fullName} has been added to this ticket`
          : `${count} participants have been added to this ticket`
      );
      setSearchQuery("");
      setSelectedCustomers([]);
      setIsAddDialogOpen(false);
    } catch (error) {
      console.error("Failed to add participants:", error);
      const reason = getErrorMessage(error, "Failed to add participant");
      showToast.error(
        "Error",
        addedCount > 0
          ? `Added ${addedCount} of ${selectedCustomers.length} participants. ${reason}`
          : reason
      );
      // Keep only the ones that weren't added selected, so a retry doesn't repeat the rest
      setSelectedCustomers(prev => prev.slice(addedCount));
    } finally {
      setIsAdding(false);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsAddDialogOpen(open);
    if (!open) {
      // Clear selection when dialog closes
      setSelectedCustomers([]);
      setSearchQuery("");
    }
  };

  const handleRemoveParticipant = async (participant: ParticipantResponse) => {
    try {
      await removeParticipantMutation.mutateAsync({
        ticketId,
        customerId: participant.customerId,
        organizationId,
      });
      showToast.success("Participant Removed", `${participant.fullName} has been removed from this ticket`);
    } catch (error) {
      console.error("Failed to remove participant:", error);
      showToast.error("Error", getErrorMessage(error, "Failed to remove participant"));
    }
  };

  const content = (
    <>
      <div className="flex items-center justify-between mb-3">
        <h4 className={embedded ? "text-sm font-medium text-muted-foreground flex items-center gap-2" : "text-lg font-semibold text-foreground flex items-center gap-2"}>
          <Users className="h-4 w-4" />
          Participants
          {participants.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">
              ({participants.length})
            </span>
          )}
        </h4>
        {!readOnly && (
          <Dialog open={isAddDialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1 h-7 text-xs">
                <UserPlus className="h-3 w-3" />
                <span className="hidden sm:inline">Add</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add Participant</DialogTitle>
                <DialogDescription>
                  Search for customers to add to this ticket. They will receive ticket notifications.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                {selectedCustomers.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    {selectedCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs"
                      >
                        <span className="truncate max-w-[120px]">{customer.fullName}</span>
                        <button
                          onClick={() => handleToggleSelection(customer)}
                          className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, or company..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {isSearching ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : eligibleParticipants.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {searchQuery
                        ? "No matching customers found"
                        : "Start typing to search for customers"}
                    </p>
                  ) : (
                    eligibleParticipants.map((customer) => {
                      const isSelected = selectedCustomers.some(c => c.id === customer.id);
                      return (
                        <button
                          key={customer.id}
                          onClick={() => handleToggleSelection(customer)}
                          className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left ${
                            isSelected
                              ? "bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700"
                              : "hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent"
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isSelected
                              ? "bg-blue-500 dark:bg-blue-600"
                              : "bg-blue-50/50 dark:bg-blue-950/50"
                          }`}>
                            {isSelected ? (
                              <Check className="h-4 w-4 text-white" />
                            ) : (
                              <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {customer.fullName}
                            </p>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              <span className="truncate">{customer.email}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
                {selectedCustomers.length > 0 && (
                  <div className="pt-4 border-t">
                    <Button
                      onClick={handleAddSelectedParticipants}
                      disabled={isAdding}
                      className="w-full"
                    >
                      {isAdding ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Add {selectedCustomers.length} Participant{selectedCustomers.length !== 1 ? "s" : ""}
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : participants.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          No participants added yet
        </p>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {participants.map((participant) => (
            <div
              key={participant.id}
              className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-blue-50/50 dark:bg-blue-950/50 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {participant.fullName}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {participant.email}
                </p>
              </div>
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveParticipant(participant)}
                  disabled={removeParticipantMutation.isPending}
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  {removeParticipantMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card className="p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
      {content}
    </Card>
  );
}
